import {useMutation, useQueryClient} from "@tanstack/react-query"
import {uploadImageForProfile} from "api/image.api.ts"
import {editCustomer} from "api/user.api"
import {useAuth} from "context/AuthContext"
import {useToast} from "hooks"
import {useEffect, useState} from "react"
import {geocodeByPlaceId} from "react-google-places-autocomplete"
import styled from "styled-components"
import {AutoCompleteInput} from "ui/base/autoCompleteInput/AutoCompleteInput"
import {ButtonPrimary} from "ui/base/Button/ButtonStyled"
import {EditAvatar} from "ui/base/editAvatar/EditAvatar"
import {InputSignUp} from "ui/base/InputSignUp/InputSignUp"
import {EmailIcon, PhoneIcon, ProfileIcon, ZipCodeIcon} from "ui/icons"
import {capitalizeFirstLetter, formatPhoneNumber, phoneNumberValidation} from "utils"
import {ChangePassword} from "./ChangePassword"
import {DeleteModal} from "./DeleteModal"
import {PrevButton} from "ui/base/Button/EmblaButtons";
import {fonts, mediaSizes} from "ui/theme";

const defaultForm = {
    firstName: "",
    lastName: "",
    addrs1: "",
    addrs2: "",
    zipCode: "",
    city: "",
    state: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
}

export const EditProfile = ({setTabToNull}) => {
    const queryClient = useQueryClient()
    const {userData} = useAuth()

    const [phoneLength, setPhoneLength] = useState(10)
    const [coordinate, setCoordinate] = useState({latitude: 0, longitude: 0})
    const [address, setAddress] = useState(null)
    const [formData, setFormData] = useState(defaultForm)
    const [image, setImage] = useState({
        name: "",
        type: "",
        uri: userData?.profile_pic,
        uploaded: true,
    })
    const [errors, setErrors] = useState({})

    useEffect(() => {
        if (userData) {
            setFormData({
                firstName: userData.first_name || "",
                lastName: userData.last_name || "",
                addrs1: userData.address1 || "",
                addrs2: userData.address2 || "",
                zipCode: userData.zip_code || "",
                city: userData.userlocation?.[0]?.city || "",
                state: userData.userlocation?.[0]?.state || "",
                email: userData.email || "",
                phone: userData.phone_number || "",
            })

            if (userData.profile_pic) {
                setImage({
                    name: "",
                    type: "",
                    uri: userData.profile_pic,
                    uploaded: true,
                })
            }

            if (userData.address1) {
                setAddress({label: userData.address1, value: userData.address1})
            }

            if (userData.userlocation?.length > 0) {
                setCoordinate({
                    latitude: userData.userlocation[0].lat || 0,
                    longitude: userData.userlocation[0].lng || 0,
                })
            }
        }
    }, [userData])

    const {mutate, isLoading, isSuccess} = useMutation({
        mutationFn: editCustomer,
        onSuccess: (data) => {
            useToast("success", "Profile update successfully")
            queryClient.invalidateQueries(["userDetail"])
        },
        onError: (err) => {
            useToast("error", "Error Updating profile")
        },
    })
    const {mutate: onUploadImage, isLoading: imageLoading} = useMutation({
        mutationFn: uploadImageForProfile,
        onError: (err) => {
            useToast("error", "Upload profile picture")
        },
        onSuccess: (data) => {
            mutate({
                address1: formData?.addrs1,
                address2: formData?.addrs2,
                city: formData?.city,
                first_name: formData?.firstName,
                last_name: formData?.lastName,
                phone_number: formData?.phone,
                state: formData?.state,
                zip_code: formData?.zipCode,
                user_type: 1,
                address_type: 1,
                lat: coordinate?.latitude ?? 0,
                lng: coordinate?.longitude ?? 0,
            })
        },
    })

    const updateCoordinate = ({latitude, longitude}) =>
        setCoordinate({latitude, longitude})

    const changeFormData = (key, value) => {
        const isError = value?.trim() === "" || value === undefined
        setFormData((prevState) => ({
            ...prevState,
            [key]: value,
        }))
        if (key === "phone") {
            value?.startsWith("+1") ? setPhoneLength(15) : setPhoneLength(12)
            setFormData((prevState) => ({
                ...prevState,
                [key]: formatPhoneNumber(value),
            }))
        }

        if (key !== "addrs2") {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [key]: {
                    value: isError,
                    text: isError ? `${capitalizeFirstLetter(key)} must be filled` : "",
                },
            }))
        }
    }

    const initLocation = () => {
        changeFormData("addrs1", "")
        changeFormData("addrs2", "")
        changeFormData("zipCode", "")
        changeFormData("city", "")
        changeFormData("state", "")
        updateCoordinate({latitude: 0, longitude: 0})
        setAddress(null)
    }

    const onChangeAddress = (newAddress) => {
        if (newAddress?.label === formData.addrs1) {
            return
        }

        setAddress(newAddress)

        if (!newAddress && formData.addrs1) {
            initLocation()
        } else if (newAddress) {
            changeFormData("addrs1", newAddress.label)
        }
    }

    const onLocationChanged = (details) => {
        if (details) {
            updateCoordinate({
                latitude: details[0].geometry.location.lat() || 0,
                longitude: details[0].geometry.location.lng() || 0,
            })

            const components = details[0].address_components
            const getComponent = (type) =>
                components.find((component) => component.types.includes(type))
                    ?.long_name || ""

            changeFormData("state", getComponent("administrative_area_level_1"))
            changeFormData(
                "city",
                getComponent("sublocality_level_1") || getComponent("locality")
            )
            changeFormData("zipCode", getComponent("postal_code").slice(0, 5))

            if (!getComponent("postal_code")) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    zipCode: {value: true, text: "Enter a valid zip code"},
                }))
            }
            if (!getComponent("locality") && !getComponent("sublocality_level_1")) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    city: {value: true, text: "Enter a valid zip code"},
                }))
            }
        } else {
            initLocation()
        }
    }

    const validateForm = (localFormData) => {
        const newErrors = {}
        Object.keys(localFormData).forEach((key) => {
            if (key !== "addrs2" && localFormData[key]?.trim?.() === "") {
                const mapObj = {
                    addrs1: "address",
                }
                newErrors[key] = {
                    value: true,
                    text: `${capitalizeFirstLetter(mapObj[key] ?? key)} must be filled`,
                }
            }

            if (
                key === "zipCode" &&
                localFormData.zipCode?.trim().length > 0 &&
                localFormData.zipCode?.trim().length < 5
            ) {
                newErrors[key] = {value: true, text: "Enter a valid zip code"}
            }

            if (
                key === "phone" &&
                localFormData.phone.length !== 12 &&
                localFormData.phone.length !== 15
            ) {
                newErrors[key] = {value: true, text: "Enter correct phone number"}
            }
        })

        setErrors((prevErrors) => ({
            ...prevErrors,
            ...newErrors,
        }))

        return Object.keys(newErrors).length === 0
    }

    const onImageUpload = (imageUri) => {
        setImage({
            name: "profilePic",
            type: "image/jpg",
            uri: imageUri,
            uploaded: false,
        })
    }

    const onSubmit = () => {
        const isValid = validateForm(formData)

        if (isValid) {
            onUploadImage({image, userType: "user"})
        }
    }

    return (
        <ProfileContainer>
            <HeaderTitleContainer>
                <PrevButton onClick={setTabToNull}/>
                <HeaderTitle>Edit profile</HeaderTitle>
            </HeaderTitleContainer>

            <EditAvatarContainer>
                <EditAvatar
                    avatar={image.uri}
                    onImageUpload={onImageUpload}
                    variant
                />
            </EditAvatarContainer>

            <ProfileRow>
                <InputSignUp
                    id='firstName'
                    value={formData.firstName}
                    setValue={(input) => changeFormData("firstName", input)}
                    placeholder='First Name'
                    icon={<ProfileIcon/>}
                    maxLength={15}
                    isError={errors?.firstName?.value}
                    errorText={"Enter first name"}
                />
                <InputSignUp
                    id='lastName'
                    value={formData?.lastName}
                    setValue={(input) => {
                        changeFormData("lastName", input)
                    }}
                    placeholder='Last Name'
                    icon={<ProfileIcon/>}
                    maxLength={15}
                    isError={errors?.lastName?.value}
                    errorText={"Enter last name"}
                />
            </ProfileRow>

            {/* google api here */}
            <AutoCompleteInput
                address={address}
                placeholder='Address'
                onChange={(e) => {
                    if (e) {
                        geocodeByPlaceId(e.value.place_id)
                            .then((results) => {
                                onChangeAddress(e)
                                onLocationChanged(results)
                            })
                            .catch((error) => {
                                initLocation()
                            })
                        return
                    }
                    setAddress({label: "", value: ""})
                    initLocation()
                }}
                isError={errors?.addrs1?.value || errors.zipCode?.value}
                errorText={errors?.addrs1?.text || errors.zipCode?.text}
            />
            <ProfileRow>
                <InputSignUp
                    type='text'
                    id='zipCode'
                    value={formData.zipCode}
                    placeholder='Zip Code'
                    readOnly
                    icon={<ZipCodeIcon/>}
                />
                <InputSignUp
                    disabled
                    type='tel'
                    id='phone'
                    value={formData.phone}
                    placeholder='Phone'
                    icon={<PhoneIcon/>}
                    setValue={(input) => {
                        changeFormData("phone", phoneNumberValidation(input))
                    }}
                    maxLength={phoneLength}
                    onBlur={() => {
                        if (formData.phone.length !== 0) {
                            if (
                                formData.phone.length !== 12 &&
                                formData.phone.length !== 15
                            ) {
                                setErrors((prevErrors) => ({
                                    ...prevErrors,
                                    phone: {
                                        value: true,
                                        text: "Enter correct phone number",
                                    },
                                }))
                            }
                        }
                    }}
                    isError={errors?.phone?.value}
                    errorText={errors?.phone?.text}
                />
            </ProfileRow>

            <InputSignUp
                disabled
                id='email'
                value={formData.email}
                placeholder='Email'
                icon={<EmailIcon/>}
                setValue={(input) => {
                    changeFormData("email", input.trim())
                }}
            />
            <ButtonContainer>
                <ChangePassword/>
                <DeleteModal/>
            </ButtonContainer>
            <div>
                <ButtonPrimary
                    disabled={imageLoading || isLoading}
                    onClick={onSubmit}>
                    Save Edits
                </ButtonPrimary>
            </div>
        </ProfileContainer>
    )
}

const ProfileContainer = styled.div`
    padding: 0.8rem;
    width: 100%;
    display: grid;
    max-width: 900px;
    margin-inline: auto;
    gap: 1.5rem;
    font-family: var(--font-secondary);
    @media (width >= ${mediaSizes.md}) {
        padding: 1rem;
    }
`
const EditAvatarContainer = styled.div`
    display: flex;
    justify-content: flex-start;
`

const ProfileRow = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
    @media (min-width: 768px) {
        grid-template-columns: 1fr 1fr;
    }
`

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const HeaderTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-bottom: 1.8rem;
    @media (width >= ${mediaSizes.md}) {
        display: none;
    }
`

const HeaderTitle = styled.div`
    font-size: ${fonts.sizes.big};
    font-weight: 600;

    @media (width >= ${mediaSizes.md}) {
        font-size: ${fonts.sizes.h4};
    }
    @media (width >= ${mediaSizes.lg}) {
        font-size: ${fonts.sizes["h3.4"]};
    }
`
